import { Table, TableBody, TableRow, Tooltip } from '@mui/material';
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { styled } from '@mui/material/styles';
import {
  StyledTableContainer,
  StyledTableHead,
  StyledHeaderCell,
  StyledHeaderRow,
  CheckboxHeaderCell,
  StyledTableRow,
  StyledBodyCell,
  EmptyResultCell,
  SortLabel,
  AppLinkedBadge,
} from '../TemporaryUserStyled';
import { formatVisitPath } from '../../../constants/visitPath';
import { getConsultMethodLabel } from '../../../constants/consultation';
import CommonCheckbox from '../../../form-components/CommonCheckbox';

const CONVERSION_RATE_LABELS = {
  VERY_HIGH: '아주높음',
  HIGH: '높음',
  MEDIUM: '중간',
  LOW: '낮음',
  VERY_LOW: '아주낮음',
};

const CONVERSION_RATE_COLORS = {
  VERY_HIGH: { bg: '#dcfce7', color: '#166534' },
  HIGH: { bg: '#d1fae5', color: '#065f46' },
  MEDIUM: { bg: '#fef9c3', color: '#854d0e' },
  LOW: { bg: '#fed7aa', color: '#9a3412' },
  VERY_LOW: { bg: '#fecaca', color: '#991b1b' },
};

const RateBadge = styled('span')(({ bg, textColor }) => ({
  display: 'inline-block',
  padding: '2px 10px',
  borderRadius: '12px',
  fontSize: '12px',
  fontWeight: 600,
  backgroundColor: bg,
  color: textColor,
}));

const MemoCell = styled('span')({
  display: 'inline-block',
  maxWidth: '180px',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
  cursor: 'default',
});

const TemporaryUserListTable = ({ managers, handleChange, sortDirection, onSortChange, onRowClick, selectAllLoading }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();

  const handleRowClick = (e, userId) => {
    e.preventDefault();
    onRowClick?.();
    navigate(`/temporary/${userId}`);
  };

  const handleSortClick = () => {
    const newDirection = sortDirection === 'ASC' ? 'DESC' : 'ASC';
    onSortChange(newDirection);
  };

  const renderConversionRate = rate => {
    if (!rate || !CONVERSION_RATE_LABELS[rate]) return '-';
    const colors = CONVERSION_RATE_COLORS[rate];
    return (
      <RateBadge bg={colors.bg} textColor={colors.color}>
        {CONVERSION_RATE_LABELS[rate]}
      </RateBadge>
    );
  };

  return (
    <StyledTableContainer>
      <Table size="small">
        <StyledTableHead>
          <StyledHeaderRow>
            <CheckboxHeaderCell align="center">
              <CommonCheckbox
                color="primary"
                name="allSelect"
                checked={managers?.content?.length > 0 && managers.content.every(manager => manager.isChecked)}
                disabled={selectAllLoading}
                onChange={handleChange}
              />
            </CheckboxHeaderCell>
            <StyledHeaderCell align="center">{t('Number')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Studio')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Name')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Gender')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('생년월일')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('Phone number')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('이메일')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('방문경로')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('상담수단')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('앱 연동')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('메모')}</StyledHeaderCell>
            <StyledHeaderCell align="center">{t('전환 확률')}</StyledHeaderCell>
            <StyledHeaderCell align="center" sortDirection={sortDirection.toLowerCase()}>
              <SortLabel active={true} direction={sortDirection.toLowerCase()} onClick={handleSortClick}>
                {t('Registration date​​')}
              </SortLabel>
            </StyledHeaderCell>
          </StyledHeaderRow>
        </StyledTableHead>
        <TableBody>
          {managers?.content?.length === 0 && (
            <TableRow>
              <EmptyResultCell colSpan={13} align="center">
                검색 결과가 없습니다.
              </EmptyResultCell>
            </TableRow>
          )}
          {managers?.content?.map((manager, index) => {
            const rowNumber = (managers.number - 1) * managers.size + index + 1;
            const memo = manager.memo || '';

            return (
              <StyledTableRow key={index} onClick={e => handleRowClick(e, manager.userId)}>
                <StyledBodyCell align="center" onClick={e => e.stopPropagation()}>
                  <CommonCheckbox
                    name={manager.name + manager.userId}
                    checked={manager?.isChecked || false}
                    onChange={handleChange}
                  />
                </StyledBodyCell>
                <StyledBodyCell align="center">{rowNumber}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.branchName || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.name || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.gender ? t(manager.gender) : '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.birthDate || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.phoneNumber || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.email || '-'}</StyledBodyCell>
                <StyledBodyCell align="center">
                  {manager.visitPath ? formatVisitPath(manager.visitPath, manager.visitPathEtc) : '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center">
                  {manager.consultMethod ? getConsultMethodLabel(manager.consultMethod) : '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center">
                  <AppLinkedBadge isLinked={manager.appLinked === 'Y'}>
                    {manager.appLinked === 'Y' ? '연동' : '미연동'}
                  </AppLinkedBadge>
                </StyledBodyCell>
                <StyledBodyCell align="center">
                  {memo ? (
                    <Tooltip title={memo} arrow slotProps={{ tooltip: { sx: { maxWidth: 300, fontSize: '13px', whiteSpace: 'pre-wrap' } } }}>
                      <MemoCell>{memo}</MemoCell>
                    </Tooltip>
                  ) : '-'}
                </StyledBodyCell>
                <StyledBodyCell align="center">{renderConversionRate(manager.conversionRate)}</StyledBodyCell>
                <StyledBodyCell align="center">{manager.registeredDate || '-'}</StyledBodyCell>
              </StyledTableRow>
            );
          })}
        </TableBody>
      </Table>
    </StyledTableContainer>
  );
};

export default TemporaryUserListTable;
